import { useEffect, useReducer, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  notification,
  Space,
  Tabs,
  Typography,
  Popconfirm,
  Row,
  Col,
  Form,
  Modal,
  Input,
  Select,
} from 'antd';

import AjaxTable from '../../components/AjaxTable';
import CustomButton from '../../components/CustomButton';

import BreadcrumbContainer from '../../containers/Breadcrumb';

import INTERNAL_LINKS from '../../enums/InternalLinks';
import MESSAGES from '../../enums/Messages';

import {
  filter as filterMass,
  remove as removeMass,
  getCounters as getCountersMass,
} from '../../api/module/mass_postcontractual';

import {
  filter,
  remove,
  getCounters,
  getIMCounters,
  getProjects,
} from '../../api/module/postcontractual';

import { ClockCircleOutlined } from '@ant-design/icons';
import PdfViewer from '../../components/PdfViewer';
import { generateTemplateURL } from '../../api/module/template';
import {
  catchException,
  catchExceptionSRate,
} from '../../utils/helpers/helpers';
import { CustomSelector } from '../../components';

const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

const breadcrumbItems = [
  {
    text: 'POSCONTRACTUAL',
  },
];

const initCounters = () => ({
  drafts: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
  created: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
  return: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
  approval: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
  returned: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
  validated: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
});

const initIMCounters = () => ({
  individual: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
  massive: (
    <ClockCircleOutlined style={{ marginLeft: '13px', marginRight: '3px' }} />
  ),
});

const initSelectors = {
  estado_proyecto: 1,
  proyecto: undefined,
};

function reducer(state, action) {
  switch (action.type) {
    case 'ps':
      return {
        ...initSelectors,
        estado_proyecto: action.payload,
      };
    case 'project':
      return {
        estado_proyecto: state.estado_proyecto,
        proyecto: action.payload,
      };

    default:
      return initSelectors;
  }
}

function PostContractualSelectors({ match, setQuery }) {
  const [state, dispatch] = useReducer(reducer, initSelectors);

  useEffect(() => {
    setQuery(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <div className="box-description" key="pa-selectors">
      <Row className="box-selectors">
        <Col span={6}>
          <Title level={4}>Estado del proyecto a revisar:</Title>
        </Col>
        <Col span={17} offset={1}>
          <Select
            options={[
              { value: 1, label: 'Activo' },
              { value: 2, label: 'Cerrado técnico' },
              { value: 3, label: 'Cerrado administrativo y jurídico' },
              { value: 0, label: 'Todos los proyectos' },
            ]}
            onChange={(value) => dispatch({ type: 'ps', payload: value })}
            style={{ width: '100%' }}
            key={match.url}
            defaultValue={initSelectors.estado_proyecto}
          />
        </Col>
      </Row>
      {(state.estado_proyecto || state.estado_proyecto === 0) && (
        <Row className="box-selectors">
          <Col span={6}>
            <Title level={4}>Proyecto a revisar:</Title>
          </Col>
          <Col span={17} offset={1}>
            <CustomSelector
              onChange={(value) =>
                dispatch({ type: 'project', payload: value })
              }
              initialValue={
                match.params.project_id
                  ? parseInt(match.params.project_id)
                  : undefined
              }
              onLoad={(value) => {
                if (match.params.project_id)
                  dispatch({
                    type: 'project',
                    payload: value ? value.value : undefined,
                  });
              }}
              selectorProps={{
                style: { width: '100%' },
                placeholder: 'Seleccione el proyecto a revisar...',
              }}
              endpointFetch={async () => {
                return await getProjects({
                  'filter[estado_proyecto]': state.estado_proyecto,
                });
              }}
              key={`rs-${state.estado_proyecto}`}
            />
          </Col>
        </Row>
      )}
    </div>
  );
}

function DeleteModal({ record, onFinish, onCancel }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  async function onSelfFinish(values) {
    setLoading(true);
    try {
      await onFinish({ id: record.id, ...values });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  if (!record) return null;

  return (
    <Modal
      centered
      title="Eliminar"
      closable={false}
      open={!!record}
      footer={
        <>
          <CustomButton
            type="default"
            htmlType="button"
            text="Cancelar"
            onClick={onCancel}
          />
          <CustomButton
            type="danger"
            text="Eliminar"
            htmlType="button"
            loading={loading}
            onClick={() => {
              form.submit();
            }}
          />
        </>
      }
    >
      {record.identificador_masivo && (
        <p>
          Este registro pertenece a la liquidación masiva:
          {record.identificador_masivo}. Se eliminaran todos los registros de
          esta.
        </p>
      )}
      <p>Por favor justifique la eliminación</p>
      <Form form={form} layout="vertical" onFinish={onSelfFinish}>
        <Form.Item
          label="Justifique la eliminación de esta liquidación"
          name="justificacion"
          rules={[{ required: true, message: 'Ingrese una justificación' }]}
        >
          <Input.TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

const PostContractualList = ({ project_id, tab = '0', perms, sendPerm }) => {
  const [counters, setCounters] = useState(initCounters);
  const [imCounters, setIMCounters] = useState(initIMCounters);
  const { Crear: createPerm, Editar: updatePerm, Borrar: deletePerm } = perms;
  const [activeTab, setActiveTab] = useState(tab);

  const [reloadSource, setReloadSource] = useState(project_id);

  const [pdfUrl, setPDFUrl] = useState(undefined);
  const [loadingPDF, setLoadingPDF] = useState(undefined);

  const [massTab, setMassTab] = useState(false);

  const [deleteRecord, setDeleteRecord] = useState(undefined);

  const getURLPDF = async (record) => {
    try {
      setLoadingPDF(record.id);
      let response = await generateTemplateURL({
        person: record.tercero,
        term: record.termino,
        contract: record.contrato,
        postcontractual: record.id,
        template_id: record.plantilla,
      });
      setPDFUrl(response.data.url);
      setLoadingPDF(null);
    } catch (err) {
      setLoadingPDF(null);
      catchException(err);
    }
  };

  const getCountersData = async () => {
    try {
      let res;
      setIMCounters(initIMCounters);
      setCounters(initCounters);
      const imRes = await getIMCounters(project_id);
      setIMCounters(imRes.data.results);
      if (massTab) res = await getCountersMass(project_id);
      else res = await getCounters(project_id);
      setCounters(res.data.results);
    } catch (err) {
      catchException(err);
    }
  };

  useEffect(() => {
    getCountersData();
    setReloadSource(project_id);
  }, [project_id, massTab]);

  const deleteDataSource = async (values) => {
    try {
      if (massTab) await removeMass(deleteRecord.id, values);
      else await remove(deleteRecord.id, values);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_DELETE_MSG,
        duration: 2,
      });
      await getCountersData();
      setReloadSource({});
      setDeleteRecord(undefined);
    } catch (err) {
      catchExceptionSRate(err, MESSAGES.DESC_ERROR_DELETE_MSG);
      throw err;
    }
  };

  const columns = [
    {
      title: 'Nombre Contratista/Proveedor',
      dataIndex: 'contratista_nombre',
    },
    {
      title: 'Tipo Documento',
      dataIndex: 'tipo_identificacion',
    },
    {
      title: 'Nro. Documento',
      dataIndex: 'identificacion',
    },
    {
      title: 'Cargo / Rol',
      dataIndex: 'cargo_nombre',
    },
    {
      title: 'No. Contrato',
      dataIndex: 'contrato_num',
    },
    {
      title: 'Tipo de terminación',
      dataIndex: 'tipo_nombre',
    },
    ...(['1', '4'].includes(activeTab)
      ? [
          {
            title: 'Enviado a',
            dataIndex: 'enviado_a',
          },
        ]
      : []),
    {
      title: 'Acciones',
      dataIndex: 'accion',
      fixed: 'right',
      width: activeTab === '5' ? 300 : 200,
      render: (_, record) => (
        <Space direction={'vertical'} style={{ width: '100%' }}>
          <CustomButton
            block
            text="Generar PDF"
            onClick={() => {
              getURLPDF(record);
            }}
            loading={loadingPDF === record.id}
            style={{ width: '100%' }}
          />
          {activeTab !== '5' && activeTab !== '4' && activeTab !== '1' && (
            <CustomButton
              block
              type="default"
              style={{ width: '100%' }}
              text={
                <Link
                  to={`${INTERNAL_LINKS.POSTCONTRACTUAL}-form/${record.id}`}
                >
                  Editar
                </Link>
              }
            />
          )}

          {activeTab !== '5' &&
            activeTab !== '4' &&
            activeTab !== '1' &&
            deletePerm && (
              <Popconfirm
                title={MESSAGES.DELETE_CONFIRM_MSG}
                onConfirm={() => {
                  setDeleteRecord(record);
                }}
                okText="Si"
                cancelText="No"
              >
                <CustomButton
                  style={{ width: '100%' }}
                  danger
                  block
                  type="default"
                  text="Eliminar"
                  loading={record.id === deleteRecord?.id}
                />
              </Popconfirm>
            )}
        </Space>
      ),
    },
  ];

  const columnsMass = [
    {
      title: 'Identificador interno de la terminación',
      dataIndex: 'identificador_masivo',
    },
    {
      title: 'Cargo / Rol',
      dataIndex: 'cargo_nombre',
    },
    {
      title: 'Tipo de terminación',
      dataIndex: 'tipo_nombre',
    },
    ...(['1', '4'].includes(activeTab)
      ? [
          {
            title: 'Enviado a',
            dataIndex: 'enviado_a',
          },
        ]
      : []),
    {
      title: 'Acciones',
      dataIndex: 'accion',
      fixed: 'right',
      width: activeTab === '5' ? 300 : 200,
      render: (_, record) => (
        <Space direction={'vertical'} style={{ width: '100%' }}>
          <CustomButton
            block
            text="Generar PDF"
            onClick={() => {
              getURLPDF(record);
            }}
            loading={loadingPDF === record.id}
            style={{ width: '100%' }}
          />
          {activeTab !== '5' && activeTab !== '4' && activeTab !== '1' && (
            <CustomButton
              block
              type="default"
              style={{ width: '100%' }}
              text={
                <Link
                  to={`${INTERNAL_LINKS.MASS_POSTCONTRACTUAL}-form/${record.id}`}
                >
                  Editar
                </Link>
              }
            />
          )}
          {activeTab === '0' && (
            <Popconfirm
              title={MESSAGES.DELETE_CONFIRM_MSG}
              onConfirm={() => {
                setDeleteRecord(record);
              }}
              okText="Si"
              cancelText="No"
            >
              <CustomButton
                style={{ width: '100%' }}
                danger
                block
                type="default"
                text="Eliminar"
                loading={record.id === deleteRecord?.id}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {createPerm ? (
        <Space>
          <CustomButton
            text={
              <Link to={`${INTERNAL_LINKS.POSTCONTRACTUAL}-new/${project_id}`}>
                Nueva terminación - Individual
              </Link>
            }
          />
          <CustomButton
            // text={
            //   <Link
            //     to={`${INTERNAL_LINKS.MASS_POSTCONTRACTUAL}-new/${project_id}`}
            //   >
            //     Nueva terminación - Masiva
            //   </Link>
            // }
            text="Nueva terminación - Masiva"
            // TODO: Remove disabled and uncomment link after mass postcontractual are online
            disabled
          />
        </Space>
      ) : null}

      <br />

      <Tabs
        type="card"
        destroyInactiveTabPane={true}
        style={{ marginTop: 15 }}
        onChange={(value) => setMassTab(parseInt(value))}
        defaultActiveKey={massTab}
      >
        <TabPane
          tab={
            <Paragraph>
              Individual
              <Badge showZero count={imCounters.individual} />
            </Paragraph>
          }
          key={0}
        >
          <Tabs
            type="card"
            activeKey={activeTab}
            destroyInactiveTabPane={true}
            onChange={(value) => {
              setActiveTab(value);
            }}
            style={{ marginTop: 15 }}
          >
            {createPerm && (
              <TabPane
                tab={
                  <Paragraph>
                    Borrador
                    <Badge showZero count={counters.drafts} />
                  </Paragraph>
                }
                key={0}
              >
                <AjaxTable
                  endpointFetch={async (page, pageSize) => {
                    return await filter(
                      `?filter[proyecto]=${project_id}&filter[tab]=0`,
                      page,
                      pageSize,
                    );
                  }}
                  columns={columns}
                  reloadSource={reloadSource}
                />
              </TabPane>
            )}

            <TabPane
              tab={
                <Paragraph>
                  En revisión
                  <Badge showZero count={counters.created} />
                </Paragraph>
              }
              key={1}
            >
              <AjaxTable
                endpointFetch={async (page, pageSize) => {
                  return await filter(
                    `?filter[proyecto]=${project_id}&filter[tab]=1`,
                    page,
                    pageSize,
                  );
                }}
                columns={columns}
                reloadSource={reloadSource}
              />
            </TabPane>

            <TabPane
              tab={
                <Paragraph>
                  Devueltos a mí
                  <Badge showZero count={counters.return} />
                </Paragraph>
              }
              key={2}
            >
              <AjaxTable
                endpointFetch={async (page, pageSize) => {
                  const thisQuery = `filter[proyecto]=${project_id}&filter[tab]=2`;
                  return await filter(`?${thisQuery}`, page, pageSize);
                }}
                columns={columns}
                reloadSource={reloadSource}
              />
            </TabPane>

            <TabPane
              tab={
                <Paragraph>
                  Para aprobación
                  <Badge showZero count={counters.approval} />
                </Paragraph>
              }
              key={3}
            >
              <AjaxTable
                endpointFetch={async (page, pageSize) => {
                  const thisQuery = `filter[proyecto]=${project_id}&filter[tab]=3`;
                  return await filter(`?${thisQuery}`, page, pageSize);
                }}
                columns={columns}
                reloadSource={reloadSource}
              />
            </TabPane>

            <TabPane
              tab={
                <Paragraph>
                  Devueltos por mí
                  <Badge showZero count={counters.returned} />
                </Paragraph>
              }
              key={4}
            >
              <AjaxTable
                endpointFetch={async (page, pageSize) => {
                  const thisQuery = `filter[proyecto]=${project_id}&filter[tab]=4`;
                  return await filter(`?${thisQuery}`, page, pageSize);
                }}
                columns={columns}
                reloadSource={reloadSource}
              />
            </TabPane>

            {sendPerm && (
              <TabPane
                tab={
                  <Paragraph>
                    Validados
                    <Badge showZero count={counters.validated} />
                  </Paragraph>
                }
                key={5}
              >
                <AjaxTable
                  endpoint={filter}
                  query={`?filter[proyecto]=${project_id}&filter[tab]=5`}
                  searchInput
                  columns={columns}
                  reloadSource={reloadSource}
                />
              </TabPane>
            )}
          </Tabs>
        </TabPane>
        <TabPane
          tab={
            <Paragraph>
              Masivo
              <Badge showZero count={imCounters.massive} />
            </Paragraph>
          }
          key={1}
        >
          <Tabs
            type="card"
            activeKey={activeTab}
            destroyInactiveTabPane={true}
            onChange={(value) => {
              setActiveTab(value);
            }}
            style={{ marginTop: 15 }}
          >
            <TabPane
              tab={
                <Paragraph>
                  Borrador
                  <Badge showZero count={counters.drafts} />
                </Paragraph>
              }
              key={0}
            >
              <AjaxTable
                endpointFetch={async (page, pageSize) => {
                  return await filterMass(
                    `?filter[proyecto]=${project_id}&filter[tab]=0`,
                    page,
                    pageSize,
                  );
                }}
                columns={columnsMass}
                reloadSource={reloadSource}
              />
            </TabPane>

            <TabPane
              tab={
                <Paragraph>
                  En revisión
                  <Badge showZero count={counters.created} />
                </Paragraph>
              }
              key={1}
            >
              <AjaxTable
                endpointFetch={async (page, pageSize) => {
                  return await filterMass(
                    `?filter[proyecto]=${project_id}&filter[tab]=1`,
                    page,
                    pageSize,
                  );
                }}
                columns={columnsMass}
                reloadSource={reloadSource}
              />
            </TabPane>

            <TabPane
              tab={
                <Paragraph>
                  Devueltos a mí
                  <Badge showZero count={counters.return} />
                </Paragraph>
              }
              key={2}
            >
              <AjaxTable
                endpointFetch={async (page, pageSize) => {
                  const thisQuery = `filter[proyecto]=${project_id}&filter[tab]=2`;
                  return await filterMass(`?${thisQuery}`, page, pageSize);
                }}
                columns={columnsMass}
                reloadSource={reloadSource}
              />
            </TabPane>

            <TabPane
              tab={
                <Paragraph>
                  Para aprobación
                  <Badge showZero count={counters.approval} />
                </Paragraph>
              }
              key={3}
            >
              <AjaxTable
                endpointFetch={async (page, pageSize) => {
                  const thisQuery = `filter[proyecto]=${project_id}&filter[tab]=3`;
                  return await filterMass(`?${thisQuery}`, page, pageSize);
                }}
                columns={columnsMass}
                reloadSource={reloadSource}
              />
            </TabPane>

            <TabPane
              tab={
                <Paragraph>
                  Devueltos por mí
                  <Badge showZero count={counters.returned} />
                </Paragraph>
              }
              key={4}
            >
              <AjaxTable
                endpointFetch={async (page, pageSize) => {
                  const thisQuery = `filter[proyecto]=${project_id}&filter[tab]=4`;
                  return await filterMass(`?${thisQuery}`, page, pageSize);
                }}
                columns={columnsMass}
                reloadSource={reloadSource}
              />
            </TabPane>

            {sendPerm && (
              <TabPane
                tab={
                  <Paragraph>
                    Validados
                    <Badge showZero count={counters.validated} />
                  </Paragraph>
                }
                key={5}
              >
                <AjaxTable
                  endpoint={filterMass}
                  query={`?filter[proyecto]=${project_id}&filter[tab]=5`}
                  searchInput
                  columns={columnsMass}
                  reloadSource={reloadSource}
                />
              </TabPane>
            )}
          </Tabs>
        </TabPane>
      </Tabs>
      <DeleteModal
        onFinish={deleteDataSource}
        onCancel={() => {
          setDeleteRecord(undefined);
        }}
        record={deleteRecord}
        key={deleteRecord?.id || 'no-key'}
      />
      <PdfViewer
        pdf={pdfUrl}
        onCancel={() => setPDFUrl(undefined)}
        open={pdfUrl}
        isWord={true}
      />
    </>
  );
};

const PostContractualHome = ({ match, perms, sendPerm }) => {
  const [query, setQuery] = useState({});

  const [params] = useState({
    tab: match.params.tab || '0',
  });

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <div className="box-title">
        <Title>MÓDULO POSCONTRACTUAL</Title>
      </div>
      <PostContractualSelectors match={match} setQuery={setQuery} />

      {query.proyecto && (
        <PostContractualList
          project_id={query.proyecto}
          tab={params.tab}
          sendPerm={sendPerm}
          perms={perms}
        />
      )}
    </>
  );
};

export default PostContractualHome;
